import { createAsyncThunk } from '@reduxjs/toolkit';
import { api } from '../services/api';
import { Car, Driver } from './types';

// Car Actions
export const fetchCars = createAsyncThunk<Car[]>(
  'cars/fetchCars',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.getCars();      
      // The API service now returns the direct array
      if (!Array.isArray(response)) {
        console.error('Response is not an array:', response);
        return [];
      }
      
      // Transform the data to match the Car type
      const cars = response.map(car => {
        const make = car.carType?.makeCompany || '';
        const model = car.carType?.model || '';
        return {
        id: car.id,
        make: make,
        model: model,
        makeModel: `${make} ${model}`,
        carType: car.carType?.type || '',
        year: car.manufacturedYear || '',
        plateNumber: car.plateNumber || '',
        capacity: car.carType?.capacity || 0,
        fuelType: car.carType?.fuelType || '',
        mileage: car.carType?.mileage || 0,
        bootSpace: car.carType?.bootSpace || 0,
        fastTagId: car.fastTagId || '',
        originalData: car
        }
      });
      
      return cars;
    } catch (error: any) {
      console.error('Error fetching cars:', error);
      return rejectWithValue(error.response?.data || 'Failed to fetch cars');
    }
  }
);

export const updateCar = createAsyncThunk<Car, { id: number; car: Car }>(
  'cars/updateCar',
  async ({ id, car }: { id: number; car: Car }, { rejectWithValue }) => {
    try {
      const response = await api.updateCar(id, car);
      // Cast response to include expected properties
      const typedResponse = response as any;
      return typedResponse as Car;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || 'Failed to update car');
    }
  }
);

export const deleteCar = createAsyncThunk<number, number>(
  'cars/deleteCar',
  async (id: number, { rejectWithValue }) => {
    try {
      await api.deleteCar(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || 'Failed to delete car');
    }
  }
);

export const addCar = createAsyncThunk<Car, Omit<Car, 'id'>>(
  'cars/addCar',
  async (car: Omit<Car, 'id'>, { rejectWithValue }) => {
    try {
      const response = await api.addCar(car);
      // Cast response to include expected properties
      const typedResponse = response as any;
      return typedResponse as Car;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || 'Failed to add car');
    }
  }
);

// Driver Actions
export const fetchDrivers = createAsyncThunk<Driver[]>(
  'drivers/fetchDrivers',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.getDrivers();
      if (!Array.isArray(response)) {
        console.error('Response is not an array:', response);
        return [];
      }
    
      // Transform the data to match the Driver type
      const drivers = response.map(driver => {
        const firstName = driver.firstName || '';
        const lastName = driver.lastName || '';
        return {
          id: driver.id,
          firstName: firstName,
          lastName: lastName,
          fullName: `${firstName} ${lastName}`,
          email: driver.email || '',
          licenseNumber: driver.licenseNumber || '',
          mobileNumber: driver.mobileNumber || '',
          photoId: driver.photoId || '',
          role: driver.role || '',
          status: driver.status || 'Available',
          originalData: driver
        };
      });
      
      return drivers;
    } catch (error: any) {
      console.error('Error fetching drivers:', error);
      return rejectWithValue(error.response?.data || 'Failed to fetch drivers');
    }
  }
);

export const updateDriver = createAsyncThunk<Driver, { id: number; driver: Driver }>(
  'drivers/updateDriver',
  async ({ id, driver }: { id: number; driver: Driver }, { rejectWithValue }) => {
    try {
      const response = await api.updateDriver(id, driver);
      // Cast response to include expected properties
      const typedResponse = response as any;
      return typedResponse as Driver;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || 'Failed to update driver');
    }
  }
);

export const deleteDriver = createAsyncThunk<number, number>(
  'drivers/deleteDriver',
  async (id: number, { rejectWithValue }) => {
    try {
      await api.deleteDriver(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || 'Failed to delete driver');
    }
  }
);

export const addDriver = createAsyncThunk<Driver, Omit<Driver, 'id'>>(
  'drivers/addDriver',
  async (driver: Omit<Driver, 'id'>, { rejectWithValue }) => {
    try {
      const response = await api.addDriver(driver);
      // Cast response to include expected properties
      const typedResponse = response as any;
      return typedResponse as Driver;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || 'Failed to add driver');
    }
  }
);
